import React, { useEffect, useState } from 'react';
import { logAuthFailure } from '../services/PreAuthLogService';
import { useLocation, useNavigate } from 'react-router-dom';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from '../firebaseConfig';

/**
 * Page de réinitialisation du mot de passe.
 * Atteinte via le lien de l'email « mot de passe oublié » (mode=resetPassword&oobCode=…),
 * redirigé depuis FirebaseAuthActionHandler. Ne passe jamais par le onboarding.
 */
const ResetPasswordPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const oobCode = new URLSearchParams(location.search).get('oobCode');

  const [status, setStatus] = useState(oobCode ? 'checking' : 'invalid'); // checking | ready | invalid | done
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!oobCode) return;
    verifyPasswordResetCode(auth, oobCode)
      .then((accountEmail) => {
        setEmail(accountEmail);
        setStatus('ready');
      })
      .catch((err) => {
        console.error('❌ Lien de réinitialisation invalide:', err.code);
        logAuthFailure('reset_link_invalid', err);
        setStatus('invalid');
      });
  }, [oobCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    if (password !== confirm) {
      setError('Les deux mots de passe ne sont pas identiques');
      return;
    }
    setSaving(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setStatus('done');
    } catch (err) {
      console.error('❌ Erreur réinitialisation mot de passe:', err.code);
      logAuthFailure('reset_confirm', err);
      if (err.code === 'auth/weak-password') {
        setError('Le mot de passe doit contenir au moins 6 caractères');
      } else if (err.code === 'auth/expired-action-code' || err.code === 'auth/invalid-action-code') {
        setStatus('invalid');
      } else {
        setError('Impossible de changer le mot de passe. Réessaie dans un instant.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-950 p-4">
      <div className="bg-slate-800/60 border border-white/10 p-8 rounded-2xl shadow-2xl w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-amber-400 font-display mb-2">Drumigo</h1>
          <p className="text-slate-300">Nouveau mot de passe</p>
        </div>

        {status === 'checking' && (
          <p className="text-center text-slate-300">Vérification du lien…</p>
        )}

        {status === 'invalid' && (
          <div className="text-center">
            <p className="text-white bg-red-700 rounded-xl px-4 py-3 mb-4 text-sm">
              Ce lien est invalide ou a expiré. Demande un nouvel email depuis « Mot de passe oublié ».
            </p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="w-full py-3 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full"
            >
              Retour à la connexion
            </button>
          </div>
        )}

        {status === 'ready' && (
          <form onSubmit={handleSubmit}>
            <p className="text-sm text-slate-300 mb-4">
              Compte : <span className="text-white font-semibold">{email}</span>
            </p>
            {error && (
              <p className="text-white bg-red-700 rounded-xl px-4 py-3 mb-4 text-sm">{error}</p>
            )}
            <label htmlFor="new-password" className="block text-slate-300 font-semibold mb-1 text-sm">
              Nouveau mot de passe
            </label>
            <input
              id="new-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
              disabled={saving}
              className="w-full px-4 py-2 mb-3 bg-slate-900/60 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50"
            />
            <label htmlFor="confirm-password" className="block text-slate-300 font-semibold mb-1 text-sm">
              Confirmer le mot de passe
            </label>
            <input
              id="confirm-password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
              disabled={saving}
              className="w-full px-4 py-2 mb-5 bg-slate-900/60 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50"
            />
            <button
              type="submit"
              disabled={saving}
              className="w-full py-3 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full disabled:opacity-60"
            >
              {saving ? 'Enregistrement…' : 'Changer mon mot de passe'}
            </button>
          </form>
        )}

        {status === 'done' && (
          <div className="text-center">
            <p className="text-white bg-green-800 rounded-xl px-4 py-3 mb-4 text-sm">
              ✅ Mot de passe modifié. Tu peux maintenant te connecter.
            </p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="w-full py-3 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full"
            >
              Se connecter
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordPage;
